import {
  Lightbulb,
  LightbulbOff,
  Thermometer,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  PersonStanding,
  Sun,
  Activity,
  Lock,
  Volume2,
  HelpCircle
} from 'lucide-react';
import {
  PREDEFINED_LOCATIONS,
  PREDEFINED_FLOORS
} from './deviceConstants.js';

export const LOCATION_ORDER = [
  'living room',
  'hall',
  'entry',
  'foyer',
  'bedroom',
  'master bedroom',
  'bedroom 1',
  'bedroom 2',
  'bedroom 3',
  'kitchen',
  'dining room',
  'bathroom',
  'bathroom 1',
  'bathroom 2',
  'balcony',
  'terrace',
  'patio',
  'garden',
  'garage',
  'office',
  'study',
  'basement',
  'attic',
  'laundry',
  'utility',
  'closet',
  'storage',
];

export const FLOOR_ORDER = [
  'basement',
  'parking',
  'ground floor',
  'first floor',
  'second floor',
  'third floor',
  'fourth floor',
  'roof',
  'top floor',
  'na',
  'not set',
];

export function sortLocations(locations) {
  return [...locations].sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    const aIndex = LOCATION_ORDER.indexOf(aLower);
    const bIndex = LOCATION_ORDER.indexOf(bLower);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return aLower.localeCompare(bLower);
  });
}

export function sortFloors(floors) {
  return [...floors].sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    const aIndex = FLOOR_ORDER.indexOf(aLower);
    const bIndex = FLOOR_ORDER.indexOf(bLower);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return aLower.localeCompare(bLower);
  });
}

export function getAllLocations(devices) {
  const locationSet = new Set(PREDEFINED_LOCATIONS);
  devices.forEach(d => {
    if (d.location && d.location.trim()) {
      locationSet.add(d.location);
    }
  });
  return sortLocations([...locationSet]);
}

export function getAllFloors(devices) {
  const floorSet = new Set(PREDEFINED_FLOORS);
  devices.forEach(d => {
    if (d.floor && d.floor.trim()) {
      floorSet.add(d.floor);
    }
  });
  return sortFloors([...floorSet]);
}

export function getDeviceUIModel(device) {
  const type = device.type?.toLowerCase() || '';

  const hasCurtainControl = device.controls?.find(c =>
    c.property === 'state' &&
    c.type === 'enum' &&
    Array.isArray(c.options?.values) &&
    c.options.values.includes('OPEN') &&
    c.options.values.includes('CLOSE')
  );

  const hasHeatingSetpoint = device.controls?.find(c =>
    c.property?.includes('current_heating_setpoint')
  );

  const hasPresenceSensor = device.sensors?.find(s => s.property === 'presence');

  const hasLockControls = device.controls?.find(c =>
    c.property === 'lock_state' || c.name?.toLowerCase().includes('lock')
  );

  let kind = type;
  let icon = HelpCircle;
  let primaryControlType = 'none';
  let isOn = false;
  let isAvailable = device.noResponse !== true;
  let primaryLabel = '';
  let secondaryLabel = '';
  let tileSize = 'small';

  if (hasLockControls || type === 'lock') {
    kind = 'lock';
    icon = Lock;
    primaryControlType = 'lock';
    tileSize = 'large';

    const lockState = device.controls?.find(c => c.property === 'lock_state');
    if (lockState) {
      isOn = lockState.value === 'UNLOCKED';
      primaryLabel = isOn ? 'Unlocked' : 'Locked';
      secondaryLabel = device.name || device.friendly_name || 'Smart Lock';
    }
  } else if (type === 'thermostat' || (!type && hasHeatingSetpoint)) {
    kind = 'thermostat';
    icon = Thermometer;
    primaryControlType = 'thermostat';
    tileSize = 'large';

    const temp = device.sensors?.find(c =>
      c.property?.includes('local_temperature') ||
      c.property?.includes('device_temperature')
    );

    const setpoint = device.controls?.find(c =>
      c.property?.includes('current_heating_setpoint')
    );

    const power = device.controls?.find(c => c.property === 'state' && c.valueType === 'binary')
      || device.controls?.find(c => c.valueType === 'binary');

    const systemMode = device.controls?.find(c =>
      c.property?.includes('system_mode')
    );

    if (temp) {
      primaryLabel = `${temp.value}°C`;
    }

    if (power) {
      isOn = power.value === true || power.value === 'ON' || power.value === power.options?.on;
    }

    // Mode only — the tile hero and stepper already show current and target
    // temperature, so repeating them here would duplicate state.
    if (power && !isOn) {
      secondaryLabel = 'Off';
    } else if (systemMode && systemMode.value) {
      secondaryLabel = systemMode.value.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    } else if (setpoint) {
      secondaryLabel = 'Thermostat';
    }
  } else if (hasCurtainControl || type === 'curtain') {
    kind = 'curtain';
    primaryControlType = 'curtain';
    tileSize = 'large';

    const curtainState = device.controls?.find(c =>
      c.property === 'state' &&
      c.type === 'enum' &&
      Array.isArray(c.options?.values)
    );

    const positionControl = device.controls?.find(c => c.property === 'position');
    const position = positionControl?.value;

    let curtainStateNormalized = 'UNKNOWN';
    let displayPosition = position;

    if (position !== null && position !== undefined) {
      if (position >= 98) {
        curtainStateNormalized = 'OPEN';
        displayPosition = 100;
      } else if (position <= 2) {
        curtainStateNormalized = 'CLOSED';
        displayPosition = 0;
      } else {
        curtainStateNormalized = 'PARTIALLY_OPEN';
      }
    } else if (curtainState?.value) {
      curtainStateNormalized = curtainState.value;
    }

    isOn = curtainStateNormalized === 'OPEN';

    switch (curtainStateNormalized) {
      case 'OPEN':
        icon = ArrowUp;
        primaryLabel = 'Open';
        break;
      case 'CLOSED':
        icon = ArrowDown;
        primaryLabel = 'Closed';
        break;
      case 'PARTIALLY_OPEN':
        icon = ArrowUpDown;
        primaryLabel = 'Open';
        break;
      default:
        icon = ArrowUpDown;
        primaryLabel = 'Unknown';
    }

    if (displayPosition !== null && displayPosition !== undefined) {
      secondaryLabel = `${displayPosition}%`;
    } else {
      secondaryLabel = device.name || device.friendly_name || 'Curtain';
    }
  } else if (type === 'sensor' || (!type && hasPresenceSensor)) {
    kind = 'sensor';
    primaryControlType = 'sensor';
    tileSize = 'small';

    const luxSensor = device.sensors?.find(s => s.property === 'illuminance');

    if (hasPresenceSensor) {
      // Occupancy / mmWave "human presence" sensor — a person figure, not a gear.
      const present = hasPresenceSensor.value === true;
      icon = PersonStanding;
      isOn = present;
      primaryLabel = present ? 'Present' : 'Absent';
    } else if (luxSensor) {
      // Ambient light sensor.
      icon = Sun;
      primaryLabel = (luxSensor.value !== null && luxSensor.value !== undefined) ? `${luxSensor.value} lux` : '';
    } else {
      // Some other measurement — don't imply a presence state it doesn't report.
      icon = Activity;
      primaryLabel = '';
    }

    secondaryLabel = device.name || device.friendly_name || 'Sensor';
    if (hasPresenceSensor && luxSensor && luxSensor.value !== null && luxSensor.value !== undefined) {
      secondaryLabel += ` • ${luxSensor.value} lux`;
    }
  } else if (type === 'light' || type === 'switch') {
    kind = type;
    primaryControlType = 'switch';
    tileSize = 'small';

    const binaryControl = device.controls?.find(c => c.valueType === 'binary');
    if (binaryControl) {
      isOn = binaryControl.value === true || binaryControl.value === 'ON' || binaryControl.value === binaryControl.options?.on;
      primaryLabel = isOn ? 'On' : 'Off';
      secondaryLabel = device.name || device.friendly_name || (type === 'light' ? 'Light' : 'Switch');

      icon = isOn ? Lightbulb : LightbulbOff;
    }
  } else if (type === 'speaker') {
    kind = 'speaker';
    icon = Volume2;
    primaryControlType = 'speaker';
    tileSize = 'small';

    const volumeControl = device.controls?.find(c => c.property === 'volume' || c.name?.toLowerCase().includes('volume'));
    if (volumeControl) {
      primaryLabel = `Vol: ${volumeControl.value}`;
      secondaryLabel = device.name || device.friendly_name || 'Speaker';
    }
  } else {
    kind = 'other';
    icon = HelpCircle;
    primaryControlType = 'unknown';
    tileSize = 'small';

    primaryLabel = device.name || device.friendly_name || 'Unknown Device';
    secondaryLabel = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Other';
  }

  const location = device.location ? `${device.location}` : '';
  const floor = device.floor ? `${device.floor}` : '';
  const fullLocation = [location, floor].filter(Boolean).join(' - ');

  return {
    kind,
    icon,
    primaryControlType,
    isOn,
    isAvailable,
    primaryLabel,
    secondaryLabel: secondaryLabel || fullLocation || 'Unknown',
    tileSize,
    fullLocation,
  };
}
